import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentSession } from '../services/authService';
import { Table, Tabs, Alert, Button, TextInput, Drawer, Pagination } from '../ui/primitives';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';
const PAGE_SIZE = 50;

const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'SENT', label: 'Sent' },
  { key: 'FAILED', label: 'Failed' },
  { key: 'SKIPPED', label: 'Skipped' },
];

const TYPE_LABELS = {
  BOOKING_CONFIRMATION_CUSTOMER: 'Booking confirmation (customer)',
  BOOKING_NOTIFICATION_OPS: 'New booking (ops)',
  BOOKING_UPDATED_CUSTOMER: 'Trip updated (customer)',
  DRIVER_CONFIRMED_CUSTOMER: 'Driver confirmed (customer)',
  DRIVER_ACCEPTED_ADMIN: 'Driver accepted (ops)',
  TRIP_COMPLETED_ACCOUNTING: 'Trip completed (accounting)',
};

const STATUS_TONES = {
  SENT: { bg: '#F0FDF4', fg: '#15803D' },
  FAILED: { bg: '#FEF2F2', fg: '#B91C1C' },
  SKIPPED: { bg: '#F2F4F7', fg: '#6B7280' },
};

function StatusPill({ status }) {
  const t = STATUS_TONES[status] || STATUS_TONES.SKIPPED;
  return (
    <span style={{
      background: t.bg, color: t.fg, padding: '2px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600,
    }}>{status}</span>
  );
}

function formatDateTime(dt) {
  if (!dt) return '-';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function typeLabel(t) {
  return TYPE_LABELS[t] || t;
}

function OpsEmailLog({ authSession }) {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');         // committed free-text filter (q)
  const [searchInput, setSearchInput] = useState(''); // text box value
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const loadedRef = useRef(false);

  const fetchLogs = useCallback(async () => {
    if (!loadedRef.current) setLoading(true);
    try {
      let token = authSession?.idToken;
      try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const params = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });
      if (status !== 'ALL') params.set('status', status);
      if (search) params.set('q', search);

      const res = await fetch(`${API_BASE}/api/email-logs?${params.toString()}`, { headers });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      setRows(data.content || []);
      setTotalPages(data.totalPages || 1);
      setTotalElements(data.totalElements ?? (data.content ? data.content.length : 0));
      loadedRef.current = true;
      setError(null);
    } catch (err) {
      setError('Failed to load email log');
    } finally {
      setLoading(false);
    }
  }, [authSession, page, status, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Reset to first page whenever the filter changes.
  const changeStatus = (s) => { setStatus(s); setPage(0); };
  const submitSearch = (e) => { e.preventDefault(); setSearch(searchInput.trim()); setPage(0); };

  const columns = [
    { key: 'when', header: 'Time', mono: true, render: (r) => formatDateTime(r.createdAt) },
    { key: 'type', header: 'Type', render: (r) => typeLabel(r.emailType) },
    { key: 'to', header: 'Recipient', render: (r) => r.toAddress || '—' },
    { key: 'booking', header: 'Booking', mono: true, render: (r) => r.bookingNumber || '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={r.status} /> },
  ];

  if (loading) return <div style={{ padding: 24, color: '#6b7280' }}>Loading email log...</div>;
  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Alert tone="danger" title="Couldn't load email log">
          <div className="flex items-center gap-3">
            <span className="flex-1">{error}</span>
            <Button variant="outline" size="sm" onClick={fetchLogs}>Retry</Button>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 4 }}>
      <form className="flex items-center gap-3" onSubmit={submitSearch}>
        <TextInput
          className="flex-1"
          placeholder="Search recipient, subject, booking #, type (press Enter)..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        <Button type="submit" variant="outline">Search</Button>
        <Button variant="outline" onClick={fetchLogs} title="Refresh">↻ Refresh</Button>
      </form>

      <div className="flex items-end justify-between">
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#05111F' }}>Email log</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>{totalElements} emails</div>
        </div>
        <Tabs tabs={STATUS_TABS} active={status} onChange={changeStatus} />
      </div>

      <Table
        columns={columns}
        rows={rows}
        empty="No emails logged yet"
        onRowClick={(r) => setSelected(r)}
      />

      {totalPages > 1 && (
        <Pagination page={page + 1} pageCount={totalPages} onChange={(p) => setPage(p - 1)} />
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="Email details">
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Row label="Status"><StatusPill status={selected.status} /></Row>
            <Row label="Type">{typeLabel(selected.emailType)}</Row>
            <Row label="To">{selected.toAddress || '—'}</Row>
            <Row label="From">{selected.fromAddress || '—'}</Row>
            <Row label="Subject">{selected.subject || '—'}</Row>
            <Row label="Booking">{selected.bookingNumber || '—'}</Row>
            <Row label="Sent at">{formatDateTime(selected.createdAt)}</Row>
            {selected.providerMessageId && <Row label="Message ID">{selected.providerMessageId}</Row>}
            {selected.errorMessage && (
              <Row label="Error"><span style={{ color: '#B91C1C' }}>{selected.errorMessage}</span></Row>
            )}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>Body</div>
              <iframe
                title="email-body"
                srcDoc={selected.body || '<p>(no body)</p>'}
                style={{ width: '100%', height: 420, border: '1px solid #e6e8ec', borderRadius: 8, background: '#fff' }}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ width: 96, flexShrink: 0, fontSize: 13, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1f2430', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

export default OpsEmailLog;
